// Documents — spec ch.06: "Left inbox, source and proposal side by side,
// highlighted source fields, corrections and history … Immutable source
// versions and evidence spans; validated proposal revisions. Highlight only
// fields with actual extraction provenance."
//
// The v3 layout is kept: an inbox on the left, and the source beside the
// proposal on the right, with the processing history under it. Three things
// differ from the demo, and each is a rule rather than a shortcut.
//
// **The invoice is the stored one, and a highlight is a real evidence span.**
// The demo draws an invoice from its own fixture and highlights fields on it.
// Acceptance V04 requires that "source highlighting identifies a real
// field/evidence span. Unsupported highlighting shows no fabricated confidence",
// so this screen renders the bytes the private bucket actually holds — fetched
// through the short-lived authorised URL that `POST /documents/{id}/source-url`
// issues — and highlights only the spans the extraction recorded, over the
// version it recorded them against. Everything it cannot locate it shows
// unhighlighted and says why. The rules are in
// apps/web/src/components/SourceViewer.tsx, where the acceptance test lives.
//
// **Approving does not book anything.** The demo flips a document to "done" on
// click. contracts/http-api.md: external writes return `202 {operationId,
// status:'queued'}` and "Only the operation read endpoint reports confirmed
// external effect." So the button's success state is *Queued*, and the screen
// then polls GET /operations/{id} and shows whatever the operation says —
// including `unknown`, which it shows as needing a status check rather than as
// a failure to retry.
//
// **Every mutation names what it was deciding about.** The expected revision
// and the approved payload hash come from the response this screen rendered,
// so approving a revision that has moved underneath answers 409 and says so,
// instead of approving whatever is newest.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, newIdempotencyKey, request, tenantPath } from '../api/client.ts';
import type {
  Accepted,
  BookedThisMonth,
  Classified,
  DocumentCheck,
  DocumentDetail,
  Excluded,
  OfficeMember,
  ProposalCoding,
  Reading,
  StatementImport,
  WorkQueue,
} from '../api/types.ts';
import { Icon } from '../components/Icon.tsx';
import { itemTitle, rowIcon } from '../components/WorkItemLine.tsx';
import { ClassifyCounterparty } from '../components/ClassifyCounterparty.tsx';
import { SupplierSuggestion } from '../components/SupplierSuggestion.tsx';
import { ExternalState } from '../components/ExternalState.tsx';
import { SourceViewer, splitFields } from '../components/SourceViewer.tsx';
import { preloadPdfJs } from '../components/PdfView.tsx';
import { StateBadge } from '../components/StateBadge.tsx';
import { UploadDocument } from '../components/UploadDocument.tsx';
import { markReplaySeen, seenReplay, useReplay } from '../components/replay.ts';
import { DOCUMENT_STATES, PROPOSAL_STATES, documentDisplay, useStates } from '../states.ts';
import { administrationFilter, useWorkspace } from '../state/workspace.tsx';
import { filterQuery, useRead } from '../state/useRead.ts';
import { useT, type MessageKey, type Translate } from '../i18n/index.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import * as format from '../format.ts';

/** How often a document waiting to be read, or being read, is asked about. */
export const READ_POLL_MS = 3000;

const CHECK_LABELS: Readonly<Record<DocumentCheck['id'], MessageKey>> = {
  vat_arithmetic: 'doc.check.vat_arithmetic',
  vat_rate_code: 'doc.check.vat_rate_code',
  supplier_mapped: 'doc.check.supplier_mapped',
  not_duplicate: 'doc.check.not_duplicate',
  region_confirmed: 'doc.check.region_confirmed',
};

const CHECK_OUTCOMES: Readonly<Record<DocumentCheck['outcome'], MessageKey>> = {
  passed: 'doc.check.outcome.passed',
  failed: 'doc.check.outcome.failed',
  not_run: 'doc.check.outcome.not_run',
};

/**
 * The duplicate guard runs at the accounting package, immediately before the
 * approved booking is sent (ch.02), so before then it has not run — and "not
 * run yet" beside "Ready for approval" read as something left undone. It says
 * when it runs instead; the clock icon still marks it as not run, and after
 * the operation the stored outcome replaces it (passed on confirmation, failed
 * on a duplicate found).
 */
function checkOutcome(check: DocumentCheck, t: Translate): string {
  if (check.id === 'not_duplicate' && check.outcome === 'not_run') return t('doc.check.beforeSending');
  return t(CHECK_OUTCOMES[check.outcome]);
}

/** When a check that has not run would run, so "not run" is never a mystery. */
const NOT_RUN_WHEN: Readonly<Record<DocumentCheck['id'], MessageKey>> = {
  vat_arithmetic: 'doc.check.when.fields',
  vat_rate_code: 'doc.check.when.fields',
  supplier_mapped: 'doc.check.when.fields',
  not_duplicate: 'doc.check.when.duplicate',
  region_confirmed: 'doc.check.when.region',
};

/** The inbox shows what is open, or what was booked this month (Today's KPI). */
export type DocumentsView = 'open' | 'booked';

export interface DocumentsScreenProps {
  readonly selectedId: string | null;
  readonly onSelect: (documentId: string | null) => void;
  readonly view?: DocumentsView;
  readonly onView?: (view: DocumentsView) => void;
}

export function DocumentsScreen({ selectedId, onSelect, view = 'open', onView }: DocumentsScreenProps): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const { token } = workspace;
  const tenantId = workspace.membership.tenantId;
  const administrationId = workspace.administrationId;

  // pdf.js and its worker start loading as the screen opens, not after a
  // document is clicked (measured: requested 68 ms and 113 ms after the click).
  useEffect(() => {
    if (typeof Worker === 'undefined') return;
    preloadPdfJs().catch(() => undefined);
  }, []);

  const [inbox, setInbox] = useState<WorkQueue['items'] | null>(null);
  const [inboxError, setInboxError] = useState<string | null>(null);
  // "Watch it read again": a request the open document answers by replaying
  // its recorded reading, offered only when there is a finished one to replay.
  const [replayAsked, setReplayAsked] = useState(0);
  const [replayable, setReplayable] = useState(false);

  const loadInbox = useCallback(async () => {
    setInboxError(null);
    try {
      const filter = administrationFilter(administrationId);
      const query = filter === undefined ? '' : `?administrationId=${encodeURIComponent(filter)}`;
      const body = await request<WorkQueue>(tenantPath(tenantId, `/work${query}`), { token });
      // The work queue is the only list endpoint there is, and for documents it
      // is the right one: it returns exactly the documents that are not
      // finished, under the caller's own grants.
      setInbox(body.items.filter((item) => item.kind === 'document'));
    } catch {
      setInbox(null);
      setInboxError(t('doc.inbox.error'));
    }
  }, [tenantId, token, administrationId, t]);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  return (
    <>
      <div className="heading between">
        <div>
          <h1>{t('doc.title')}</h1>
          <p>{t('doc.subtitle')}</p>
        </div>
        {selectedId !== null && replayable ? (
          <button type="button" className="btn" onClick={() => setReplayAsked((count) => count + 1)}>
            <Icon name="refresh" size={15} />
            {t('doc.replay.button')}
          </button>
        ) : null}
      </div>

      <div className="workbench">
        <div className="card">
          {onView === undefined ? null : (
            <div className="seg doc-views" role="group" data-testid="documents-views">
              {(['open', 'booked'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={view === option}
                  data-testid={`documents-view-${option}`}
                  onClick={() => onView(option)}
                >
                  {t(option === 'open' ? 'documents.view.open' : 'documents.view.booked')}
                </button>
              ))}
            </div>
          )}
          {view === 'booked' ? (
            <BookedList selectedId={selectedId} onSelect={onSelect} />
          ) : (
          <>
          <div className="card-head">
            <div>
              {/* "Inbox" is the same word in Dutch. */}
              <h2>Inbox</h2>
              <p>{t('doc.inbox.subtitle')}</p>
            </div>
            <span className="badge">{inbox?.length ?? 0}</span>
          </div>
          {inboxError !== null ? (
            <p className="error-banner" role="alert" style={{ margin: 16 }}>
              {inboxError}
            </p>
          ) : inbox === null ? (
            <div className="empty">
              <p>{t('doc.loading')}</p>
            </div>
          ) : inbox.length === 0 ? (
            <div className="empty">
              <h3>{t('doc.inbox.emptyTitle')}</h3>
              <p>{t('doc.inbox.emptyText')}</p>
            </div>
          ) : (
            inbox.map((item) => {
              const icon = rowIcon(item);
              const kind = item.summary?.mediaType?.startsWith('image/') === true ? t('doc.kind.photo') : item.summary?.mediaType === 'application/pdf' ? 'PDF' : null;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`row${item.id === selectedId ? ' selected' : ''}`}
                  onClick={() => onSelect(item.id)}
                >
                  <span className={`ri ${icon.tone}`} aria-hidden="true">
                    <Icon name={icon.name} />
                  </span>
                  <div className="grow">
                    <strong>{item.summary?.supplierName ?? itemTitle(item)}</strong>
                    <p>
                      {[item.summary?.invoiceNumber ?? null, kind, format.relative(item.orderedAt)]
                        .filter((part) => part !== null)
                        .join(' · ')}
                    </p>
                  </div>
                  {/* The inbox row is narrow: the short label, with the full
                      label and its explanation as the title. The document's
                      own header keeps the full label. */}
                  <StateBadge table={DOCUMENT_STATES} state={item.state} short />
                </button>
              );
            })
          )}
          {/* The drop zone sits with the inbox it feeds, as in the design: the
              same POST /intake on an approved upload route as before. */}
          <UploadDocument
            tenantId={tenantId}
            token={token}
            onUploaded={(documentId) => {
              void loadInbox();
              onSelect(documentId);
            }}
          />
          </>
          )}
        </div>

        {selectedId === null ? (
          <div className="card">
            <div className="empty">
              <h3>{t('doc.select.title')}</h3>
              <p>{t('doc.select.text')}</p>
            </div>
          </div>
        ) : (
          <DocumentWorkbench
            key={selectedId}
            documentId={selectedId}
            onChanged={() => void loadInbox()}
            replayAsked={replayAsked}
            onReplayable={setReplayable}
          />
        )}
      </div>
    </>
  );
}

/**
 * What was booked this month: exactly the documents Today's "booked" figure
 * counts (GET /insights/booked uses the same query). A row opens the same
 * document view as the inbox does.
 */
function BookedList({
  selectedId,
  onSelect,
}: {
  readonly selectedId: string | null;
  readonly onSelect: (documentId: string | null) => void;
}): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const booked = useRead<BookedThisMonth>(
    `/insights/booked${filterQuery(administrationFilter(workspace.administrationId))}`,
    'the documents booked this month',
  );
  const documents = booked.data?.documents ?? [];
  return (
    <div data-testid="booked-list">
      <div className="card-head">
        <div>
          <h2>{t('documents.view.booked')}</h2>
        </div>
        <span className="badge" data-testid="booked-total">
          {booked.data?.total ?? 0}
        </span>
      </div>
      {booked.error !== null && booked.data === null ? (
        <p className="error-banner" role="alert" style={{ margin: 16 }}>
          {booked.error}
        </p>
      ) : booked.data === null ? (
        <div className="empty">
          <p>{t('common.reading')}</p>
        </div>
      ) : documents.length === 0 ? (
        <EmptyState text={t('documents.booked.empty')} testId="booked-empty" icon="done" />
      ) : (
        <>
          {documents.map((row) => (
            <button
              key={row.documentId}
              type="button"
              className={`row${row.documentId === selectedId ? ' selected' : ''}`}
              data-testid={`booked-${row.documentId}`}
              onClick={() => onSelect(row.documentId)}
            >
              <span className="ri gr" aria-hidden="true">
                <Icon name="done" />
              </span>
              <div className="grow">
                <strong>{row.supplierName ?? row.invoiceNumber ?? '—'}</strong>
                <p>
                  {[row.invoiceNumber, format.relative(row.confirmedAt)]
                    .filter((part) => part !== null)
                    .join(' · ')}
                </p>
              </div>
              {row.total === null ? null : <span className="amt">{format.money(row.total)}</span>}
            </button>
          ))}
          {booked.data.total > documents.length ? (
            <p className="card-foot">{t('documents.booked.more', { shown: documents.length, total: booked.data.total })}</p>
          ) : null}
        </>
      )}
    </div>
  );
}

type ActionState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'working' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'excluded' }
  | { readonly kind: 'classified'; readonly classification: string };

function DocumentWorkbench({
  documentId,
  onChanged,
  replayAsked,
  onReplayable,
}: {
  readonly documentId: string;
  readonly onChanged: () => void;
  /** Bumped by "Watch it read again". */
  readonly replayAsked: number;
  readonly onReplayable: (replayable: boolean) => void;
}): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const states = useStates();
  const personId = workspace.me.user.id;
  const { token } = workspace;
  const tenantId = workspace.membership.tenantId;

  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<ActionState>({ kind: 'idle' });
  const [watchedOperationId, setWatchedOperationId] = useState<string | null>(null);

  const load = useCallback(async (background = false) => {
    if (!background) setError(null);
    try {
      const body = await request<DocumentDetail>(
        tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}`),
        { token },
      );
      setDetail(body);
    } catch (caught) {
      // A failed poll says nothing new about the document; keep what is shown.
      if (background) return;
      setDetail(null);
      setError(
        caught instanceof ApiError && caught.isAbsent ? t('doc.error.absent') : t('doc.error.read'),
      );
    }
  }, [tenantId, token, documentId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // While the document is waiting to be read or being read, ask again: the
  // reading is a durable job, and its state on the document is the only signal
  // that it has started or finished. Nothing animates once it has stopped.
  const documentState = detail?.document.state;
  const beingRead = documentState === 'received' || documentState === 'extracting';
  useEffect(() => {
    if (!beingRead) return undefined;
    const timer = setInterval(() => {
      void load(true);
    }, READ_POLL_MS);
    return () => clearInterval(timer);
  }, [beingRead, load]);
  const wasBeingRead = useRef(false);
  useEffect(() => {
    // Finishing a reading changes the inbox too (its state badge).
    if (wasBeingRead.current && !beingRead) onChanged();
    wasBeingRead.current = beingRead;
  }, [beingRead, onChanged]);

  // The replay of the finished reading. It plays by itself the first time this
  // person opens this document — including straight after watching it being
  // read live — and again whenever they ask. It replays the RECORD: the fields
  // as stored, lit at their recorded spans, and the checks as they came out.
  const finishedReading = detail !== null && !beingRead && detail.extraction !== null;
  const [replayRun, setReplayRun] = useState(0);
  useEffect(() => {
    onReplayable(finishedReading);
  }, [finishedReading, onReplayable]);
  useEffect(() => {
    if (!finishedReading || seenReplay(personId, documentId)) return;
    markReplaySeen(personId, documentId);
    setReplayRun((run) => run + 1);
  }, [finishedReading, personId, documentId]);
  const firstAsk = useRef(replayAsked);
  useEffect(() => {
    if (replayAsked === firstAsk.current) return;
    firstAsk.current = replayAsked;
    setReplayRun((run) => run + 1);
  }, [replayAsked]);
  const replay = useReplay(
    finishedReading ? replayRun : 0,
    splitFields(detail?.extraction?.fields ?? []).headline.length,
    detail?.checks.length ?? 0,
  );

  const approve = useCallback(async () => {
    if (detail?.proposal == null) return;
    const proposal = detail.proposal;
    if (proposal.payloadHash === null || proposal.sourceVersionId === null) {
      setAction({
        kind: 'error',
        message: t('doc.actions.noRevision'),
      });
      return;
    }
    setAction({ kind: 'working' });
    // One key for one intention: a second click sends the same key and gets the
    // first answer back rather than creating a second operation.
    const idempotencyKey = newIdempotencyKey();
    try {
      const accepted = await request<Accepted>(
        tenantPath(tenantId, `/proposals/${encodeURIComponent(proposal.id)}/approve`),
        {
          method: 'POST',
          token,
          idempotencyKey,
          body: {
            expectedRevision: proposal.currentRevision,
            proposalHash: proposal.payloadHash,
            sourceVersionId: proposal.sourceVersionId,
            decision: 'approve_submission',
          },
        },
      );
      setAction({ kind: 'idle' });
      setWatchedOperationId(accepted.operationId);
      await load();
      onChanged();
    } catch (caught) {
      setAction({ kind: 'error', message: approvalMessage(caught, t) });
      // A conflict means the screen is out of date; re-read so the reviewer is
      // looking at what the server has rather than at what they clicked on.
      if (caught instanceof ApiError && caught.isStale) await load();
    }
  }, [detail, tenantId, token, load, onChanged, t]);

  const exclude = useCallback(
    async (reason: string, evidenceRef: string) => {
      if (detail === null) return;
      setAction({ kind: 'working' });
      const idempotencyKey = newIdempotencyKey();
      try {
        await request<Excluded>(
          tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/exclude`),
          {
            method: 'POST',
            token,
            idempotencyKey,
            body: {
              // The document's revision is the number of its current immutable
              // source version, which is what the endpoint checks against.
              expectedRevision: detail.history.versions[0]?.version ?? 0,
              reason,
              evidenceRef,
            },
          },
        );
        setAction({ kind: 'excluded' });
        await load();
        onChanged();
      } catch (caught) {
        setAction({ kind: 'error', message: approvalMessage(caught, t) });
        if (caught instanceof ApiError && caught.isStale) await load();
      }
    },
    [detail, documentId, tenantId, token, load, onChanged, t],
  );

  const classify = useCallback(
    async (classification: 'one_off' | 'recurring_business') => {
      if (detail === null) return;
      setAction({ kind: 'working' });
      const idempotencyKey = newIdempotencyKey();
      try {
        const answered = await request<Classified>(
          tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/classify-counterparty`),
          {
            method: 'POST',
            token,
            idempotencyKey,
            body: {
              expectedRevision: detail.history.versions[0]?.version ?? 0,
              classification,
            },
          },
        );
        setAction({ kind: 'classified', classification: answered.classification });
        await load();
        onChanged();
      } catch (caught) {
        setAction({ kind: 'error', message: approvalMessage(caught, t) });
        if (caught instanceof ApiError && caught.isStale) await load();
      }
    },
    [detail, documentId, tenantId, token, load, onChanged, t],
  );

  /**
   * One of the answers about a document (refinement 6): POST, then read the result back.
   * An answer that queues an external write (the credit-note settlement, a 202) is
   * then watched in External state exactly like an approval.
   */
  const answer = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      if (detail === null) return;
      setAction({ kind: 'working' });
      try {
        const answered = await request<Partial<Accepted> | undefined>(
          tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/${path}`),
          {
            method: 'POST',
            token,
            idempotencyKey: newIdempotencyKey(),
            body: { expectedRevision: detail.history.versions[0]?.version ?? 0, ...body },
          },
        );
        if (typeof answered?.operationId === 'string') setWatchedOperationId(answered.operationId);
        setAction({ kind: 'idle' });
        await load();
        onChanged();
      } catch (caught) {
        setAction({ kind: 'error', message: approvalMessage(caught, t) });
        if (caught instanceof ApiError && caught.isStale) await load();
      }
    },
    [detail, documentId, tenantId, token, load, onChanged, t],
  );

  const checkRegion = useCallback(async () => {
    if (detail === null) return;
    setAction({ kind: 'working' });
    try {
      await request<{ documentId: string; state: string }>(
        tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/region-check`),
        {
          method: 'POST',
          token,
          idempotencyKey: newIdempotencyKey(),
          body: { expectedRevision: detail.history.versions[0]?.version ?? 0 },
        },
      );
      setAction({ kind: 'idle' });
      await load();
      onChanged();
    } catch (caught) {
      setAction({ kind: 'error', message: approvalMessage(caught, t) });
      if (caught instanceof ApiError && caught.isStale) await load();
    }
  }, [detail, documentId, tenantId, token, load, onChanged, t]);

  const confirmSupplier = useCallback(
    async (relationId: string, ledgerId: string, vatDeductible: boolean) => {
      if (detail === null) return;
      setAction({ kind: 'working' });
      try {
        await request(tenantPath(tenantId, `/documents/${encodeURIComponent(documentId)}/supplier-rule`), {
          method: 'POST',
          token,
          idempotencyKey: newIdempotencyKey(),
          body: {
            expectedRevision: detail.history.versions[0]?.version ?? 0,
            relationId,
            ledgerId,
            ...(vatDeductible ? {} : { vatDeductible: false }),
          },
        });
        setAction({ kind: 'idle' });
        await load();
        onChanged();
      } catch (caught) {
        setAction({ kind: 'error', message: approvalMessage(caught, t) });
        if (caught instanceof ApiError && caught.isStale) await load();
      }
    },
    [detail, documentId, tenantId, token, load, onChanged, t],
  );

  if (error !== null) {
    return (
      <div className="card">
        <div className="card-body">
          <p className="error-banner" role="alert">
            {error}
          </p>
        </div>
      </div>
    );
  }
  if (detail === null) {
    return (
      <div className="card">
        <div className="empty">
          <p>{t('doc.loadingDocument')}</p>
        </div>
      </div>
    );
  }

  const { document: source, proposal, extraction, blockers, history, externalState } = detail;
  const approvable = proposal !== null && proposal.status === 'ready' && blockers.length === 0;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>
              {[proposal?.invoiceNumber ?? null, supplierOf(extraction)].filter((part) => part !== null).join(' · ') ||
                documentTypeName(source.documentType, t)}
            </h2>
            <p>
              {[
                channelName(source.sourceProvider, t),
                t('doc.header.received', { when: format.dateTime(source.receivedAt) }),
                history.versions[0]?.pageCount == null
                  ? null
                  : t('doc.header.pages', { count: history.versions[0].pageCount }),
                history.versions[0] === undefined ? null : sizeOf(history.versions[0].byteSize, t),
              ]
                .filter((part) => part !== null)
                .join(' · ')}
            </p>
          </div>
          {source.state === 'extracting' ? (
            <span className="badge blue" role="status">
              <span className="live" aria-hidden="true" style={{ background: 'var(--blue)' }} />
              {detail.regionCheck?.state === 'pending' ? t('doc.header.regionWaiting') : t('doc.header.reading')}
            </span>
          ) : (
            <StateBadge shown={documentDisplay(source.state, source.booking)} />
          )}
        </div>

        <div className="card-body">
          {replay.playing && extraction !== null ? (
            <p className="replay-note" role="status" data-testid="replay-note">
              <Icon name="refresh" size={13} />
              <b>{t('doc.replay.title')}</b>
              <span className="muted">{replayPace(extraction, t)}</span>
            </p>
          ) : null}

          {/* The stored bytes, and only real evidence spans over them. The
              viewer decides what it may highlight; see V04 there. */}
          <SourceViewer
            tenantId={tenantId}
            documentId={documentId}
            token={token}
            versions={history.versions}
            extraction={extraction}
            scanning={source.state === 'extracting'}
            reveal={replay.playing ? replay.fields : null}
            replayRun={replayRun}
            aside={
              detail.checks.length === 0 ? null : (
                <ChecksPanel checks={detail.checks} shown={replay.playing ? replay.checks : null} />
              )
            }
          />

          {detail.statement != null ? (
            <section style={{ marginTop: 18 }}>
              <h3>{t('doc.statement.title')}</h3>
              <StatementPanel statement={detail.statement} />
            </section>
          ) : null}

          {blockers.length === 0 ? null : (
            <div className="callout warn" style={{ marginTop: 14 }}>
              <b>{t('doc.blockers.title')}</b>
              <ul style={{ margin: '8px 0 0 18px' }}>
                {blockers.map((blocker, index) => (
                  <li key={`${blocker.code}:${index}`}>
                    {states.blockerLabel(blocker.code, blocker.reason)}
                    {blocker.field === null ? '' : ` · ${blocker.field}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {detail.regionCheck?.canCheckAgain === true ? (
            <RegionCheckPanel
              check={detail.regionCheck}
              working={action.kind === 'working'}
              onCheck={() => void checkRegion()}
            />
          ) : null}

          {(detail.warnings ?? []).length === 0 ? null : (
            <div className="callout warn" style={{ marginTop: 14 }} data-testid="warnings">
              <b>{t('doc.warnings.title')}</b>
              <ul style={{ margin: '8px 0 0 18px' }}>
                {(detail.warnings ?? []).map((warning, index) => (
                  <li key={`${warning.code}:${index}`}>
                    {states.blockerLabel(warning.code, warning.reason)}
                    {warning.field === null ? '' : ` · ${warning.field}`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {blockers.some((blocker) => blocker.code === 'PAYMENT_METHOD_UNKNOWN') ? (
            <PaymentMethodChoice working={action.kind === 'working'} onChoose={(method) => void answer('payment-method', { method })} />
          ) : null}

          {detail.vatSuggestion == null ? null : (
            <div className="callout ai" style={{ marginTop: 14 }} data-testid="vat-suggestion">
              <b>{t('doc.vat.didYouMean', { candidate: detail.vatSuggestion.candidate })}</b>
              <p style={{ margin: '6px 0 0' }}>
                {t('doc.vat.explain', {
                  read: detail.vatSuggestion.read,
                  relation: detail.vatSuggestion.relation.name ?? t('doc.vat.anyRelation'),
                  code: detail.vatSuggestion.relation.code === null ? '' : ` (${detail.vatSuggestion.relation.code})`,
                })}
              </p>
              <div className="actions" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn"
                  disabled={action.kind === 'working'}
                  onClick={() => void answer('vat-number', { vatNumber: detail.vatSuggestion?.candidate })}
                >
                  {t('doc.vat.use', { candidate: detail.vatSuggestion.candidate })}
                </button>
              </div>
            </div>
          )}

          {detail.settlement == null ? null : (
            <SettlementPanel
              settlement={detail.settlement}
              working={action.kind === 'working'}
              onApprove={() => void answer('credit-settlement', {})}
            />
          )}

          {detail.supplierSuggestion == null ? null : (
            <SupplierSuggestion
              suggestion={detail.supplierSuggestion}
              working={action.kind === 'working'}
              onConfirm={(relationId, ledgerId, vatDeductible) => void confirmSupplier(relationId, ledgerId, vatDeductible)}
            />
          )}

          {blockers.some((blocker) => blocker.code === 'COUNTERPARTY_UNCLASSIFIED') ? (
            <ClassifyCounterparty
              working={action.kind === 'working'}
              onClassify={(choice) => void classify(choice)}
            />
          ) : null}

          {/* A statement never has a proposal, so it is not offered one. */}
          {detail.statement != null ? null : (
            <ProposalSummary
              proposal={proposal}
              supplier={supplierOf(extraction)}
              shown={!replay.playing || replay.summary}
            />
          )}

          {extraction === null ? null : <RouteLine extraction={extraction} />}

          <details className="reading-details">
            <summary>{t('doc.details.summary')}</summary>
            <dl className="kv" style={{ marginTop: 10 }}>
              <dt>{t('doc.details.direction')}</dt>
              <dd>{directionName(source.sourceDirection, t)}</dd>
              <dt>{t('doc.details.type')}</dt>
              <dd>{source.documentType === null ? t('doc.details.notDetermined') : documentTypeName(source.documentType, t)}</dd>
              <dt>{t('doc.details.hash')}</dt>
              <dd>
                <code style={{ fontSize: 11 }}>{source.contentHash.slice(0, 32)}…</code>
              </dd>
              <dt>{t('doc.details.versions')}</dt>
              <dd>
                {history.versions.length === 0
                  ? t('doc.details.noVersions')
                  : history.versions.map((version) => `v${version.version} · ${version.mimeType}`).join(', ')}
              </dd>
              {extraction === null ? null : <ReadingRows reading={extraction.reading} />}
            </dl>
          </details>
        </div>

        <div className="card-foot">
          <Actions
            approvable={approvable}
            excludable={source.state !== 'excluded'}
            action={action}
            onApprove={() => void approve()}
            onExclude={(reason, evidence) => void exclude(reason, evidence)}
          />
        </div>
      </div>

      <ExternalState
        operations={externalState.operations}
        watchedOperationId={watchedOperationId}
        tenantId={tenantId}
        token={token}
        stillRunningHint={t('doc.external.stillRunning')}
        onSettled={() => {
          void load();
          onChanged();
        }}
      />

      <ProcessingHistory detail={detail} />
    </div>
  );
}

/** What a decision kind is called in the trail. */
const DECISION_TITLES: Readonly<Record<string, MessageKey>> = {
  approve_submission: 'doc.history.approve_submission',
  reject: 'doc.history.reject',
  exclude: 'doc.history.exclude',
  verify_outcome: 'doc.history.verify_outcome',
  approve_settlement: 'doc.history.approve_settlement',
};

const CLASSIFICATIONS: Readonly<Record<string, MessageKey>> = {
  one_off: 'doc.history.one_off',
  recurring_business: 'doc.history.recurring_business',
};

/**
 * The document's trail, oldest first: received, each source version, every
 * reading ("Read", then "Read again"), people's actions with their names
 * (a supplier rule saved, a counterparty classified), and the decisions.
 * People are named as the office named them, or by address until then —
 * never by a guess; someone not in the list is "a colleague".
 */
function ProcessingHistory({ detail }: { readonly detail: DocumentDetail }): ReactNode {
  const workspace = useWorkspace();
  const t = useT();
  const users = useRead<{ users: readonly OfficeMember[] }>('/users', 'the colleagues');
  const people = new Map((users.data?.users ?? []).map((member) => [member.id, member]));
  const who = (id: string | null) => {
    if (id === null) return t('doc.history.system');
    if (id === workspace.me.user.id) return t('doc.history.you');
    const person = people.get(id);
    return person?.displayName ?? person?.email ?? t('doc.history.colleague');
  };
  const { document: source, history } = detail;
  const entries: { key: string; at: string; title: string; meta: string }[] = [
    { key: 'received', at: source.receivedAt, title: t('doc.history.received'), meta: channelName(source.sourceProvider, t) },
    ...history.versions.map((version) => ({
      key: `version:${version.id}`,
      at: version.createdAt,
      title: t('doc.history.version', { version: version.version }),
      meta: version.mimeType,
    })),
    ...(history.events ?? []).map((event, index) => {
      if (event.kind === 'reading') {
        const outcome =
          event.outcome === 'proposal'
            ? t('doc.history.proposal')
            : event.outcome === 'stopped'
              ? t('doc.history.stopped', { count: event.blockers ?? 0 })
              : t('doc.history.unreadable');
        // 0038: a reading that settles a held answer is not a new call to
        // the model — it is the earlier answer, its region now known (or,
        // failed, never reported in time).
        const title = event.heldAnswer === true
          ? event.outcome === 'failed'
            ? t('doc.history.regionLate')
            : t('doc.history.regionConfirmed')
          : (event.readingNumber ?? 1) > 1
            ? t('doc.history.readAgain')
            : t('doc.history.read');
        return {
          key: `event:${index}`,
          at: event.at,
          title,
          meta: [event.modelVersion, outcome].filter(Boolean).join(' · '),
        };
      }
      if (event.kind === 'settlement_confirmed') {
        return {
          key: `event:${index}`,
          at: event.at,
          title: t('doc.history.settlementConfirmed', { mutation: event.mutation ?? '—' }),
          meta: 'e-Boekhouden',
        };
      }
      if (event.kind === 'supplier_rule_saved') {
        return {
          key: `event:${index}`,
          at: event.at,
          title: t('doc.history.ruleSaved', { who: who(event.actorUserId) }),
          meta: [
            event.relationId === undefined ? null : t('doc.history.relation', { relation: event.relationId }),
            event.ledger ?? null,
            event.vatDeductible === false ? t('doc.history.vatNotDeductible') : null,
          ]
            .filter(Boolean)
            .join(' · '),
        };
      }
      return {
        key: `event:${index}`,
        at: event.at,
        title: t('doc.history.classified', { who: who(event.actorUserId) }),
        meta: event.classification === undefined ? '' : nameOr(CLASSIFICATIONS[event.classification], event.classification, t),
      };
    }),
    // A classification is shown from its event above, which carries the answer.
    ...history.decisions
      .filter((decision) => decision.kind !== 'classify_counterparty')
      .map((decision) => ({
        key: `decision:${decision.id}`,
        at: decision.decidedAt,
        title: t('doc.history.by', {
          title: nameOr(DECISION_TITLES[decision.kind], t('doc.history.decision', { kind: decision.kind.replace('_', ' ') }), t),
          who: who(decision.actorUserId),
        }),
        meta: decision.reason ?? '',
      })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('doc.history.title')}</h2>
      </div>
      <div className="card-body">
        <div className="trace" data-testid="processing-history">
          {entries.map((entry) => (
            <p key={entry.key}>
              <b>{entry.title}</b>
              <br />
              {[entry.meta, format.dateTime(entry.at)].filter((part) => part !== '').join(' · ')}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

function Actions({
  approvable,
  excludable,
  action,
  onApprove,
  onExclude,
}: {
  readonly approvable: boolean;
  readonly excludable: boolean;
  readonly action: ActionState;
  readonly onApprove: () => void;
  readonly onExclude: (reason: string, evidenceRef: string) => void;
}): ReactNode {
  const t = useT();
  const [excluding, setExcluding] = useState(false);
  const [reason, setReason] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const busy = action.kind === 'working';

  if (action.kind === 'excluded') {
    return (
      <span className="muted small">
        {t('doc.actions.excluded')}
      </span>
    );
  }

  return (
    <div className="stack">
      {action.kind === 'error' ? (
        <p className="error-banner" role="alert">
          {action.message}
        </p>
      ) : null}

      <div className="between">
        <span className="muted small">
          {t('doc.actions.explain')}
        </span>
        <div className="flex">
          {excludable ? (
            <button type="button" className="btn" onClick={() => setExcluding((open) => !open)}>
              {t('doc.actions.exclude')}
            </button>
          ) : null}
          <button type="button" className="btn primary" disabled={!approvable || busy} onClick={onApprove}>
            {busy ? t('doc.actions.submitting') : t('doc.actions.approve')}
          </button>
        </div>
      </div>

      {excluding ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim() !== '' && evidenceRef.trim() !== '') {
              onExclude(reason.trim(), evidenceRef.trim());
            }
          }}
        >
          {/* Both required by contracts/http-api.md: "reason and evidence
              required". The endpoint refuses without them; asking here means the
              reviewer finds out before the request rather than after. */}
          <label className="field">
            {t('doc.actions.reason')}
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('doc.actions.reasonPlaceholder')}
              aria-label={t('doc.actions.reason')}
            />
          </label>
          <label className="field">
            {t('doc.actions.evidence')}
            <input
              value={evidenceRef}
              onChange={(event) => setEvidenceRef(event.target.value)}
              placeholder={t('doc.actions.evidencePlaceholder')}
              aria-label={t('doc.actions.evidence')}
            />
          </label>
          <button
            type="submit"
            className="btn danger"
            disabled={busy || reason.trim() === '' || evidenceRef.trim() === ''}
          >
            {t('doc.actions.excludeDocument')}
          </button>
        </form>
      ) : null}
    </div>
  );
}

/** The supplier the latest reading found, if it found one. */
function supplierOf(extraction: DocumentDetail['extraction']): string | null {
  return extraction?.fields.find((field) => field.fieldPath === 'supplierName')?.value ?? null;
}

/** "2 KB", from the stored byte size (a decimal string). */
function sizeOf(bytes: string, t: Translate): string {
  const value = Number(bytes);
  if (!Number.isFinite(value)) return t('doc.size.bytes', { size: bytes });
  if (value < 1024) return t('doc.size.bytes', { size: value });
  if (value < 1024 * 1024) return t('doc.size.kb', { size: format.count(Math.round(value / 1024)) });
  return t('doc.size.mb', { size: format.number(value / (1024 * 1024), 1) });
}

/** The translated name behind a key, or the stored value itself when there is none. */
function nameOr(key: MessageKey | undefined, fallback: string, t: Translate): string {
  return key === undefined ? fallback : t(key);
}

const DOCUMENT_TYPE_KEYS: Readonly<Record<string, MessageKey>> = {
  invoice: 'doc.type.invoice',
  credit_note: 'doc.type.credit_note',
  receipt: 'doc.type.receipt',
};

/** The stored document type, named; "Document" when none was determined, an unknown one as stored. */
function documentTypeName(type: string | null, t: Translate): string {
  if (type === null) return 'Document';
  return nameOr(DOCUMENT_TYPE_KEYS[type], type, t);
}

const DIRECTION_KEYS: Readonly<Record<string, MessageKey>> = {
  inbound: 'doc.direction.inbound',
  outbound: 'doc.direction.outbound',
};

function directionName(direction: string, t: Translate): string {
  return nameOr(DIRECTION_KEYS[direction], direction, t);
}

function money(value: { amount: string; currency: string } | null): string {
  // Decimal strings, printed as they arrived. ch.02: "Do not use JavaScript
  // floating-point arithmetic for financial decisions" — and parsing one into a
  // Number to format it is exactly that, one layer out.
  // format.money inserts the Dutch separators into the exact text; no Number.
  return value === null ? '—' : format.money(value);
}

function approvalMessage(caught: unknown, t: Translate): string {
  if (!(caught instanceof ApiError)) return t('doc.actions.failed');
  if (caught.isStale) return t('doc.actions.stale');
  if (caught.status === 403) return t('doc.actions.forbidden');
  if (caught.isAbsent) return t('doc.actions.absent');
  if (caught.status === 422) {
    return t('doc.actions.refused', {
      fields: caught.fieldErrors.map((f) => f.field).join(', ') || t('doc.actions.validation'),
    });
  }
  return caught.message;
}

/** "extraction.lines[0].net" → "Line 1 net"; "extraction.total" → "total". */
function fieldLabel(path: string, t: Translate): string {
  return path
    .replace(/^extraction\./, '')
    .replace(/^lines\[(\d+)\]\./, (_m, i: string) => t('doc.details.line', { number: Number(i) + 1 }))
    .replace(/^taxBreakdown\[(\d+)\]\./, (_m, i: string) => t('doc.details.vatRow', { number: Number(i) + 1 }));
}

/** The provider's record of where a call ran, and what its lookup found. */
const ROUTE_CHECK_LABELS: Readonly<Record<string, MessageKey>> = {
  reported: 'doc.routeCheck.reported',
  not_found: 'doc.routeCheck.not_found',
  incomplete: 'doc.routeCheck.incomplete',
  http_error: 'doc.routeCheck.http_error',
  network: 'doc.routeCheck.network',
  not_json: 'doc.routeCheck.not_json',
};

/**
 * How the text was obtained and where the model ran — the evidence behind every
 * field in the proposal. Nothing here is defaulted: an unrecorded route says so,
 * and unread pages are named, because a proposal made without page 2 must not
 * look like one made from the whole document.
 */
/**
 * The checks, each with what the records say happened to it. A failed check
 * shows as failed; one that never ran says so and when it would.
 */
function ChecksPanel({
  checks,
  shown = null,
}: {
  readonly checks: readonly DocumentCheck[];
  /** During a replay, how many checks have appeared; null when all are shown. */
  readonly shown?: number | null;
}): ReactNode {
  const t = useT();
  const states = useStates();
  return (
    <div style={{ marginTop: 16 }}>
      <h3 className="read-title">{t('doc.checks.title')}</h3>
      <ul className="checks" style={{ marginTop: 8 }}>
        {checks.map((check, index) => (
          <li
            key={check.id}
            className={`check ${check.outcome}${shown === null || index < shown ? ' on' : ' pending'}`}
            data-testid={`check-${check.id}`}
            title={
              check.outcome === 'not_run'
                ? t(NOT_RUN_WHEN[check.id])
                : check.codes.length === 0
                  ? undefined
                  : check.codes.map((code) => states.blockerLabel(code)).join('; ')
            }
          >
            <Icon name={check.outcome === 'passed' ? 'check' : check.outcome === 'failed' ? 'alert' : 'clock'} size={14} />
            {t(CHECK_LABELS[check.id])}: {checkOutcome(check, t)}
          </li>
        ))}
      </ul>
      {checks.some((check) => check.outcome === 'failed') ? (
        <p className="muted small" style={{ marginTop: 8 }}>
          {checks
            .filter((check) => check.outcome === 'failed')
            .map((check) => `${t(CHECK_LABELS[check.id])}: ${check.codes.map((code) => states.blockerLabel(code)).join('; ')}`)
            .join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The design's proposal strip: how the current revision books. Every cell is
 * from the revision itself — the ledger named as the last sync mirrored the
 * chart, the VAT code as the revision carries it — or says what is missing.
 */
function ProposalSummary({
  proposal,
  supplier,
  shown,
}: {
  readonly proposal: DocumentDetail['proposal'];
  readonly supplier: string | null;
  readonly shown: boolean;
}): ReactNode {
  const t = useT();
  const states = useStates();
  if (proposal === null) {
    return (
      <p className="muted small" style={{ marginTop: 16 }}>
        {t('doc.proposal.none')}
      </p>
    );
  }
  const coding = proposal.coding ?? [];
  const ledgers = [...new Set(coding.map((line) => ledgerName(line, t)))];
  const taxCodes = [...new Set(coding.map(taxCodeName).filter((code): code is string => code !== null))];
  return (
    <div className={`prop${shown ? ' on' : ''}`} data-testid="proposal-summary" aria-hidden={shown ? undefined : true}>
      <div className="prop-head">
        <span className="read-title">{t('doc.proposal.title')}</span>
        <StateBadge table={PROPOSAL_STATES} state={proposal.status} />
        <span className="muted small">{t('doc.proposal.revision', { revision: proposal.currentRevision })}</span>
      </div>
      <div className="prop-grid">
        <div>
          <div className="pl">{t('doc.proposal.booksAs')}</div>
          <div className="pv">{booksAs(proposal, t)}</div>
        </div>
        <div>
          <div className="pl">
            {t(
              ledgers.length > 1
                ? 'doc.proposal.ledgers'
                : proposal.direction === 'sales'
                  ? 'doc.proposal.revenueLedger'
                  : 'doc.proposal.costLedger',
            )}
          </div>
          <div className="pv">{ledgers.length === 0 ? '—' : ledgers.join(', ')}</div>
        </div>
        {proposal.paidFrom == null ? null : (
          <div data-testid="paid-from">
            <div className="pl">{t('doc.proposal.paidFrom')}</div>
            <div className="pv">{ledgerName(proposal.paidFrom, t)}</div>
          </div>
        )}
        <div>
          <div className="pl">{t('doc.proposal.vatCode')}</div>
          <div className="pv">{taxCodes.length === 0 ? '—' : taxCodes.join(', ')}</div>
        </div>
        <div>
          <div className="pl">{t('doc.proposal.supplier')}</div>
          <div className="pv">{supplier ?? '—'}</div>
        </div>
        <div>
          <div className="pl">{t('doc.proposal.total')}</div>
          <div className="pv">{money(proposal.total)}</div>
        </div>
      </div>
      {proposal.vatNotDeducted == null ? null : (
        <p className="small" style={{ marginTop: 10 }} data-testid="vat-not-deducted">
          <b>{t('doc.proposal.vatNotReclaimed', { amount: money(proposal.vatNotDeducted) })}</b>{' '}
          <span className="muted">{t('doc.proposal.vatNotReclaimedWhy')}</span>
        </p>
      )}
      <p className="muted small" style={{ marginTop: 10 }}>
        {states.display(PROPOSAL_STATES, proposal.status).explanation}
      </p>
    </div>
  );
}

/** What a proposal books as, per direction and document type ("Purchase invoice"). */
const BOOKS_AS: Readonly<Record<'purchase' | 'sales', Readonly<Record<string, MessageKey>>>> = {
  purchase: {
    invoice: 'doc.booksAs.purchase.invoice',
    credit_note: 'doc.booksAs.purchase.credit_note',
    receipt: 'doc.booksAs.purchase.receipt',
    document: 'doc.booksAs.purchase.document',
  },
  sales: {
    invoice: 'doc.booksAs.sales.invoice',
    credit_note: 'doc.booksAs.sales.credit_note',
    receipt: 'doc.booksAs.sales.receipt',
    document: 'doc.booksAs.sales.document',
  },
};

const BOOKS_AS_UNDIRECTED: Readonly<Record<string, MessageKey>> = {
  invoice: 'doc.booksAs.none.invoice',
  credit_note: 'doc.booksAs.none.credit_note',
  receipt: 'doc.booksAs.none.receipt',
};

function booksAs(proposal: NonNullable<DocumentDetail['proposal']>, t: Translate): string {
  const type = proposal.documentType ?? 'document';
  if (proposal.direction === 'purchase' || proposal.direction === 'sales') {
    const key = BOOKS_AS[proposal.direction][type];
    return key === undefined ? t(`doc.booksAs.${proposal.direction}.other`, { kind: type }) : t(key);
  }
  if (type === 'document') return 'Document';
  const key = BOOKS_AS_UNDIRECTED[type];
  return key === undefined ? type.charAt(0).toUpperCase() + type.slice(1) : t(key);
}

/** "7000 Inkopen" from the synced chart; the reference alone, said as such, before a sync. */
function ledgerName(coding: Pick<ProposalCoding, 'ledgerRef' | 'ledgerCode' | 'ledgerName'>, t: Translate): string {
  if (coding.ledgerName != null && coding.ledgerName !== '') return `${coding.ledgerCode ?? ''} ${coding.ledgerName}`.trim();
  if (coding.ledgerCode != null) return t('doc.ledger.code', { code: coding.ledgerCode });
  return coding.ledgerRef === null ? '—' : t('doc.ledger.ref', { ref: coding.ledgerRef });
}

/** "21% · HOOG_INK_21": the rate and the package's own code, both from the revision. */
function taxCodeName(coding: ProposalCoding): string | null {
  if (coding.taxCode === null && coding.taxRate === null) return null;
  const rate = coding.taxRate === null ? null : `${format.decimal(coding.taxRate).replace(/,0+$/, '')}%`;
  return [rate, coding.taxCode].filter((part) => part !== null).join(' · ');
}

/** "How was this receipt paid?" — only when the receipt does not say. */
function PaymentMethodChoice({
  working,
  onChoose,
}: {
  readonly working: boolean;
  readonly onChoose: (method: 'cash' | 'bank') => void;
}): ReactNode {
  const t = useT();
  const [method, setMethod] = useState<'cash' | 'bank' | null>(null);
  return (
    <div className="callout" style={{ marginTop: 14 }} data-testid="payment-method">
      <b>{t('doc.payment.title')}</b>
      <p style={{ margin: '6px 0 0' }}>{t('doc.payment.text')}</p>
      <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
        <label>
          <input type="radio" name="payment-method" checked={method === 'cash'} onChange={() => setMethod('cash')} />{' '}
          {t('doc.payment.cash')}
        </label>
        <label>
          <input type="radio" name="payment-method" checked={method === 'bank'} onChange={() => setMethod('bank')} />{' '}
          {t('doc.payment.bank')}
        </label>
      </div>
      <div className="actions" style={{ marginTop: 10 }}>
        <button type="button" className="btn" disabled={working || method === null} onClick={() => method !== null && onChoose(method)}>
          {t('doc.payment.submit')}
        </button>
      </div>
    </div>
  );
}

/** A booked credit note set off against its original: a separate decision, no money moving. */
function SettlementPanel({
  settlement,
  working,
  onApprove,
}: {
  readonly settlement: NonNullable<DocumentDetail['settlement']>;
  readonly working: boolean;
  readonly onApprove: () => void;
}): ReactNode {
  const t = useT();
  if (settlement.operation !== null) {
    return (
      <p className="muted" style={{ marginTop: 14 }} data-testid="settlement">
        {t('doc.settlement.state', { state: settlement.operation.state })}
      </p>
    );
  }
  if (settlement.offer === 'offered' && settlement.amount != null) {
    return (
      <div className="callout" style={{ marginTop: 14 }} data-testid="settlement">
        <b>{t('doc.settlement.offerTitle', { number: settlement.originalInvoiceNumber ?? '' })}</b>
        <p style={{ margin: '6px 0 0' }}>{t('doc.settlement.offerText', { amount: format.money(settlement.amount) })}</p>
        <div className="actions" style={{ marginTop: 10 }}>
          <button type="button" className="btn" disabled={working} onClick={onApprove}>
            {t('doc.settlement.approve')}
          </button>
        </div>
      </div>
    );
  }
  const why: Record<string, MessageKey> = {
    not_booked: 'doc.settlement.not_booked',
    no_original: 'doc.settlement.no_original',
    not_released: 'doc.settlement.not_released',
    ledger_not_configured: 'doc.settlement.ledger_not_configured',
  };
  const key = why[settlement.offer];
  const text = key === undefined ? undefined : t(key);
  return text === undefined ? null : (
    <p className="muted" style={{ marginTop: 14 }} data-testid="settlement">
      {text}
    </p>
  );
}

/**
 * 0038: the model answered, and the provider had not yet said where it ran.
 * The answer is held and not used; the system keeps asking, and a person may
 * ask once more. Nothing is sent again either way.
 */
function RegionCheckPanel({
  check,
  working,
  onCheck,
}: {
  readonly check: NonNullable<DocumentDetail['regionCheck']>;
  readonly working: boolean;
  readonly onCheck: () => void;
}): ReactNode {
  const t = useT();
  const pending = check.state === 'pending';
  return (
    <div className={`callout ${pending ? 'ai' : 'warn'}`} style={{ marginTop: 14 }} data-testid="region-check">
      <b>{pending ? t('doc.region.pendingTitle') : t('doc.region.neverTitle')}</b>
      <p style={{ margin: '6px 0 0' }}>
        {t('doc.region.held')}{' '}
        {t('doc.region.asked', { count: check.checks, at: format.dateTime(check.lastCheckedAt) })}{' '}
        {pending
          ? check.nextCheckAt === null
            ? ''
            : t('doc.region.next', {
                next: format.dateTime(check.nextCheckAt),
                deadline: format.dateTime(check.deadlineAt),
              })
          : t('doc.region.askOnce')}
      </p>
      <div className="actions" style={{ marginTop: 10 }}>
        <button type="button" className="btn" disabled={working} onClick={onCheck}>
          {t('doc.region.button')}
        </button>
      </div>
    </div>
  );
}

/** Where this reading was done, as recorded: the model, the host, who served it, the region check. */
function RouteLine({ extraction }: { readonly extraction: NonNullable<DocumentDetail['extraction']> }): ReactNode {
  const t = useT();
  const reading = extraction.reading;
  const served = [reading.modelServedBy, reading.modelRegion === null ? null : capitalise(reading.modelRegion)]
    .filter((part): part is string => part != null && part !== '')
    .join(', ');
  const parts = [
    t('doc.route.readBy', {
      model: reading.modelVersion ?? t('doc.route.unrecordedModel'),
      host: reading.modelHost ?? '',
    }),
    served === '' ? null : served,
    reading.routeCheck === null
      ? null
      : reading.routeCheck.outcome === 'reported'
        ? t('doc.route.confirmed')
        : t('doc.route.notConfirmed'),
  ].filter((part): part is string => part !== null);
  return (
    <p className="route-line" data-testid="route-line">
      <Icon name="shield" size={14} />
      {parts.join(' · ')}
    </p>
  );
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** What the replay is not: the real pace. "The reading itself took 4,2 s, on 21 okt 10:18." */
function replayPace(extraction: NonNullable<DocumentDetail['extraction']>, t: Translate): string {
  const started = extraction.reading.startedAt;
  const finished = extraction.completedAt;
  if (started === undefined || finished === null) return t('doc.replay.stored');
  const seconds = (Date.parse(finished) - Date.parse(started)) / 1000;
  if (!Number.isFinite(seconds) || seconds < 0) return t('doc.replay.stored');
  const took = seconds < 0.1 ? t('doc.replay.under') : `${format.number(seconds, 1)} s`;
  return t('doc.replay.took', { took, when: format.dateTime(finished) });
}

function ReadingRows({ reading }: { readonly reading: Reading }) {
  const t = useT();
  const states = useStates();
  return (
    <>
      <dt>{t('doc.details.readAs')}</dt>
      <dd>
        {reading.route === null ? t('doc.details.notRecorded') : states.textRoute(reading.route)}
        {reading.tool === null ? null : <small className="muted"> · {reading.tool}</small>}
      </dd>
      {reading.pageCount === null ? null : (
        <>
          <dt>{t('doc.details.pages')}</dt>
          <dd>
            {reading.pageCount}
            {reading.unreadPages.length === 0 ? null : (
              <span className="warn-text">
                {' '}
                · {t('doc.details.unreadPages', { pages: reading.unreadPages.join(', '), count: reading.unreadPages.length })}
              </span>
            )}
          </dd>
        </>
      )}
      {reading.ocrConfidence === null ? null : (
        <>
          <dt>{t('doc.details.ocr')}</dt>
          <dd>{reading.ocrConfidence}%</dd>
        </>
      )}
      {reading.modelHost === null ? null : (
        <>
          <dt>{t('doc.details.modelRoute')}</dt>
          <dd>
            {reading.modelHost}
            {reading.modelRegion === null ? null : ` · ${reading.modelRegion}`}
          </dd>
        </>
      )}
      {reading.conversions.length === 0 ? null : (
        <>
          <dt>{t('doc.details.converted')}</dt>
          <dd>
            <span className="small">{t('doc.details.convertedIntro')}</span>
            <ul className="small" style={{ margin: '4px 0 0 18px' }}>
              {reading.conversions.map((conversion) => (
                <li key={conversion.field}>
                  {fieldLabel(conversion.field, t)}: <code>{conversion.from}</code> → <code>{conversion.to}</code>
                  {conversion.rules.includes('date_day_first') ? <small className="muted"> {t('doc.details.dayFirst')}</small> : null}
                </li>
              ))}
            </ul>
          </dd>
        </>
      )}
      {reading.routeCheck === null ? null : (
        <>
          <dt>{t('doc.details.regionCheck')}</dt>
          <dd className={reading.routeCheck.outcome === 'reported' ? undefined : 'warn-text'}>
            {nameOr(ROUTE_CHECK_LABELS[reading.routeCheck.outcome], reading.routeCheck.outcome, t)}
            <small className="muted">
              {' '}
              · {t('doc.details.lookups', { count: reading.routeCheck.attempts })}
              {reading.routeCheck.lastStatus === null ? '' : t('doc.details.lastHttp', { status: reading.routeCheck.lastStatus })}
            </small>
          </dd>
        </>
      )}
    </>
  );
}

/**
 * A bank statement's import. The balances are the bank's own and were proved to
 * add up before a single line was stored; the counts say what is NEW, so a file
 * imported twice reads as "nothing new" rather than as twice the transactions.
 * Nothing here is a payment: candidates wait in Bank matching for a person.
 */
function StatementPanel({ statement }: { readonly statement: StatementImport }) {
  const t = useT();
  return (
    <>
      {statement.status === 'blocked' ? (
        <p className="muted small" style={{ marginTop: 6 }}>
          {t('doc.stmt.notImported')}
        </p>
      ) : (
        <dl className="kv" style={{ marginTop: 6 }}>
          <dt>{t('doc.stmt.lines')}</dt>
          <dd>
            {t('doc.stmt.lineCounts', { count: statement.lineCount, new: statement.newLineCount })}
            {statement.lineCount > statement.newLineCount ? (
              <small className="muted">
                {' '}
                · {t('doc.stmt.alreadyStored', { count: statement.lineCount - statement.newLineCount })}
              </small>
            ) : null}
          </dd>
          <dt>{t('doc.stmt.candidates')}</dt>
          <dd>
            {statement.candidateCount === 0
              ? t('doc.stmt.noneFound')
              : t('doc.stmt.offered', { count: statement.candidateCount })}
          </dd>
          <dt>{t('doc.stmt.openItems')}</dt>
          <dd>{statement.openItemsSynced === null ? t('doc.stmt.notChecked') : statement.openItemsSynced}</dd>
        </dl>
      )}
      {statement.statements.map((entry, index) => (
        <dl className="kv" style={{ marginTop: 12 }} key={`${entry.account}:${index}`}>
          <dt>{t('doc.stmt.account')}</dt>
          <dd>
            <code style={{ fontSize: 12 }}>{entry.accountIban ?? entry.account}</code>
            {entry.sequence === null ? null : <small className="muted"> · {t('doc.stmt.sequence', { sequence: entry.sequence })}</small>}
          </dd>
          <dt>{t('doc.stmt.period')}</dt>
          <dd>
            {format.date(entry.opening.date)} – {format.date(entry.closing.date)}
          </dd>
          <dt>{t('doc.stmt.balance')}</dt>
          <dd>
            {money(entry.opening)} → {money(entry.closing)}
            <small className="muted"> · {t('doc.stmt.provedLines', { count: entry.lineCount })}</small>
          </dd>
        </dl>
      ))}
    </>
  );
}

/**
 * How a document came in, in words: the intake channel (`upload`, `mailbox`,
 * `webhook`, stored as the document's source provider). Dutch gets a word for
 * the two a customer sees; English keeps the stored value as it always showed,
 * and anything else (`webhook`, a value this screen does not know) stays raw.
 */
export function channelName(channel: string, t: Translate): string {
  if (channel === 'upload') return t('doc.channel.upload');
  if (channel === 'mailbox') return t('doc.channel.mailbox');
  return channel;
}
